import { useEffect, useMemo, useState } from 'react'
import { adminApi, adminLogin } from '../lib/api.js'
import { aud } from '../lib/format.js'
import { ADMIN_I18N, ADMIN_LANGS } from '../lib/adminI18n.js'
import { focusStyle, imageFocus, withImageFocus, imageOption, withImageOption, imageStep, withImageStep } from '../lib/imageFocus.js'
import { isAddUp, parseValueSpec, formatValueSpec } from '../lib/optionPricing.js'
import { CATEGORIES as SITE_CATEGORIES } from '../data/site.js'
import Logo from '../components/Logo.jsx'

const CATEGORIES = SITE_CATEGORIES.map((c) => c.name)

// One-click option names. "(mm)" makes the storefront append mm to each value.
const OPTION_PRESETS = [
  'Hardware Colour', 'Width (mm)', 'Height (mm)', 'Glass Thickness (mm)', 'Size', 'Doors', 'Panel',
]

// Thumbnail that says "photo missing" instead of showing a broken-image icon when a
// stored URL no longer loads — so the owner knows to upload the photo again.
function AdminImg({ src, t }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => { setFailed(false) }, [src])
  if (!src) return <span>{t.noImage}</span>
  if (failed) return <span className="admin-img-missing">{t.imageMissing}</span>
  return <img src={src} alt="" style={focusStyle(src)} onError={() => setFailed(true)} />
}

// dropdown that picks which part of the photo stays visible when the frame crops it
function FocusSelect({ src, onChange, t }) {
  return (
    <select
      className="admin-focus-select"
      value={imageFocus(src)}
      onChange={(e) => onChange(withImageFocus(src, e.target.value))}
      title={t.fImgFocus}
    >
      <option value="">{t.focusDefault}</option>
      <option value="top">{t.focusTop}</option>
      <option value="bottom">{t.focusBottom}</option>
      <option value="left">{t.focusLeft}</option>
      <option value="right">{t.focusRight}</option>
    </select>
  )
}

// dropdown that tags a photo with the option value it represents, so the storefront
// swaps to this photo when the customer selects that value
function OptionTagSelect({ src, values, onChange, t }) {
  const current = imageOption(src)
  return (
    <select
      className="admin-focus-select"
      value={current}
      onChange={(e) => onChange(withImageOption(src, e.target.value))}
      title={t.imgOptionTag}
    >
      <option value="">{t.imgOptionNone}</option>
      {values.map((v) => <option key={v} value={v}>{v}</option>)}
      {current && !values.includes(current) && <option value={current}>{current}</option>}
    </select>
  )
}

// dropdown that ties a photo to a configurator step, so the storefront shows this
// diagram while the customer is on that step (e.g. which panel "Doors Panel Width" means)
function StepTagSelect({ src, names, onChange, t }) {
  const current = imageStep(src)
  return (
    <select
      className="admin-focus-select"
      value={current}
      onChange={(e) => onChange(withImageStep(src, e.target.value))}
      title={t.imgStepTag}
    >
      <option value="">{t.imgStepNone}</option>
      {names.map((n) => <option key={n} value={n}>{n}</option>)}
      {current && !names.includes(current) && <option value={current}>{current}</option>}
    </select>
  )
}

function LangSwitch({ lang, setLang }) {
  return (
    <div className="lang-switch" role="group" aria-label="Language">
      {ADMIN_LANGS.map((l) => (
        <button
          key={l.id}
          className={lang === l.id ? 'active' : ''}
          onClick={() => setLang(l.id)}
        >
          {l.label}
        </button>
      ))}
    </div>
  )
}

export default function Admin() {
  // Two ways in: a staff account (email + password -> session token) or the shared
  // master key, which is the only way in before the first account exists.
  const [key, setKey] = useState(localStorage.getItem('lugs_admin_key') || '')
  const [token, setToken] = useState(localStorage.getItem('lugs_admin_token') || '')
  const [me, setMe] = useState(null)
  const [authed, setAuthed] = useState(false)
  const [mode, setMode] = useState('account')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState('orders')
  const [error, setError] = useState('')
  const [lang, setLangState] = useState(localStorage.getItem('lugs_admin_lang') || 'th')
  const setLang = (l) => { localStorage.setItem('lugs_admin_lang', l); setLangState(l) }
  const t = ADMIN_I18N[lang] || ADMIN_I18N.th
  const api = useMemo(() => adminApi({ key, token }), [key, token])

  useEffect(() => {
    if (!key && !token) return
    adminApi({ key, token }).login()
      .then((d) => { setMe(d.user || null); setAuthed(true) })
      .catch(() => {
        // A dead session token would keep failing every request — drop it and ask again.
        localStorage.removeItem('lugs_admin_token')
        setToken('')
        setAuthed(false)
      })
  }, []) // eslint-disable-line

  async function loginAccount(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const { token: newToken, user } = await adminLogin(email.trim(), password)
      localStorage.setItem('lugs_admin_token', newToken)
      localStorage.removeItem('lugs_admin_key')
      setKey('')
      setToken(newToken)
      setMe(user)
      setPassword('')
      setAuthed(true)
    } catch (err) {
      setError(err.message || t.loginWrongUser)
    } finally {
      setBusy(false)
    }
  }

  async function loginMaster(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const d = await adminApi({ key }).login()
      localStorage.setItem('lugs_admin_key', key)
      localStorage.removeItem('lugs_admin_token')
      setToken('')
      setMe(d.user || null)
      setAuthed(true)
    } catch {
      setError(t.loginWrong)
    } finally {
      setBusy(false)
    }
  }

  async function logout() {
    try { if (token) await api.logout() } catch { /* the token is going away regardless */ }
    localStorage.removeItem('lugs_admin_key')
    localStorage.removeItem('lugs_admin_token')
    setKey('')
    setToken('')
    setMe(null)
    setAuthed(false)
  }

  if (!authed) {
    return (
      <main className="admin admin-login">
        <form className="panel" onSubmit={mode === 'account' ? loginAccount : loginMaster}>
          <div className="admin-login-top">
            <Logo size={44} />
            <LangSwitch lang={lang} setLang={setLang} />
          </div>
          <h1>{t.loginTitle}</h1>
          {mode === 'account' ? (
            <>
              <div className="field">
                <label htmlFor="admin-email">{t.loginEmail}</label>
                <input id="admin-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
              </div>
              <div className="field">
                <label htmlFor="admin-password">{t.loginPasswordUser}</label>
                <input id="admin-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </>
          ) : (
            <div className="field">
              <label htmlFor="admin-key">{t.loginPassword}</label>
              <input id="admin-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoFocus />
            </div>
          )}
          {error && <p className="field-error">{error}</p>}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>{t.loginButton}</button>
          <button
            type="button"
            className="admin-link-btn"
            onClick={() => { setError(''); setMode(mode === 'account' ? 'master' : 'account') }}
          >
            {mode === 'account' ? t.loginUseMaster : t.loginUseAccount}
          </button>
        </form>
      </main>
    )
  }

  return (
    <main className="admin">
      <header className="admin-header">
        <Logo size={36} />
        <nav>
          <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>{t.tabOrders}</button>
          <button className={tab === 'products' ? 'active' : ''} onClick={() => setTab('products')}>{t.tabProducts}</button>
          <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>{t.tabUsers}</button>
          <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>{t.tabSettings}</button>
        </nav>
        {me && <span className="admin-whoami">{t.loggedInAs(me.name || me.email)}</span>}
        <LangSwitch lang={lang} setLang={setLang} />
        <button className="btn btn-outline btn-sm" onClick={logout}>{t.logout}</button>
      </header>
      {tab === 'orders' && <OrdersTab api={api} t={t} me={me} />}
      {tab === 'products' && <ProductsTab api={api} t={t} />}
      {tab === 'users' && <UsersTab api={api} t={t} me={me} onReauth={logout} />}
      {tab === 'settings' && <SettingsTab api={api} t={t} />}
    </main>
  )
}

/* ---------------- Orders ---------------- */

// The slip lives behind the admin key, so it is fetched as a blob on demand
// rather than linked — one order card at a time, never all of them at once.
function OrderReceipt({ api, id, type, t }) {
  const [src, setSrc] = useState('')
  const [error, setError] = useState('')

  useEffect(() => () => { if (src) URL.revokeObjectURL(src) }, [src])

  async function show() {
    try {
      const { url } = await api.orderReceipt(id)
      if (type === 'application/pdf') window.open(url, '_blank', 'noopener')
      else setSrc(url)
    } catch {
      setError(t.receiptFailed)
    }
  }

  return (
    <div className="admin-receipt">
      {src
        ? <a href={src} target="_blank" rel="noopener"><img src={src} alt={t.receiptTitle} /></a>
        : <button className="btn btn-outline btn-sm" onClick={show}>🧾 {t.receiptView}</button>}
      {error && <p className="field-error">{error}</p>}
    </div>
  )
}

// Re-sends a paid order's tax invoice — used when the first attempt failed (e.g. the
// sending domain was not verified yet) or the customer lost the email.
function ResendInvoice({ api, id, to, sent, t }) {
  const [state, setState] = useState('')
  async function send() {
    if (!confirm(t.confirmResendInvoice(to))) return
    setState('sending')
    try {
      await api.resendInvoice(id)
      setState('done')
    } catch (err) {
      setState(err.message || 'failed')
    }
  }
  if (state === 'done') return <span className="admin-invoice-ok">{t.invoiceSent(to)}</span>
  return (
    <>
      <button className="btn btn-outline btn-sm" onClick={send} disabled={state === 'sending'}>
        {state === 'sending' ? t.invoiceSending : (sent ? t.btnResendInvoice : t.btnSendInvoice)}
      </button>
      {state && !['sending', 'done'].includes(state) && <span className="field-error">{state}</span>}
    </>
  )
}

// Who looks after this customer. Any admin can take an order; the owner can hand one
// to someone else. Changing a status also claims the order, so this is mostly for
// planning ahead ("คนนี้ให้ตูมดูแล") rather than record-keeping after the fact.
function AssignRow({ api, order, users, me, t, onDone }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function assign(id) {
    setBusy(true)
    setError('')
    try {
      await api.assignOrder(order.id, id)
      await onDone()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const listed = users.filter((u) => u.active || u.id === order.handled_by)
  return (
    <p className="admin-handler">
      👤 {t.handledBy}: <strong>{order.handled_by_name || t.handledByNone}</strong>
      <select value={order.handled_by || ''} disabled={busy} onChange={(e) => assign(e.target.value)}>
        <option value="">{t.assignNobody}</option>
        {/* the master key has no account row, so keep its own option when it holds the order */}
        {order.handled_by === 'master' && <option value="master">{order.handled_by_name}</option>}
        {listed.map((u) => (
          <option key={u.id} value={u.id}>
            {(u.name || u.email) + (u.active ? '' : ` (${t.userDisabled})`)}
          </option>
        ))}
      </select>
      {me && !me.master && order.handled_by !== me.id && (
        <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => assign(me.id)}>{t.assignToMe}</button>
      )}
      {error && <span className="field-error">{error}</span>}
    </p>
  )
}

// Everything the staff did to this order, newest first. Loaded on demand — 300 orders
// worth of history in the list response would be most of the payload.
function OrderHistory({ api, id, t }) {
  const [log, setLog] = useState(null)
  const [open, setOpen] = useState(false)

  async function toggle() {
    if (open) { setOpen(false); return }
    setOpen(true)
    if (log === null) {
      try {
        const d = await api.orderLog(id)
        setLog(d.log)
      } catch {
        setLog([])
      }
    }
  }

  const ACTIONS = { status: t.actStatus, assign: t.actAssign, invoice: t.actInvoice }
  return (
    <div className="admin-history">
      <button className="btn btn-outline btn-sm" onClick={toggle}>{open ? t.historyHide : t.historyShow}</button>
      {open && (log === null ? <p className="muted">{t.loading}</p> :
        log.length === 0 ? <p className="muted">{t.historyEmpty}</p> : (
          <ul className="admin-history-list">
            {log.map((row) => (
              <li key={row.id}>
                <span className="muted">{new Date(row.created).toLocaleString(t.dateLocale, { dateStyle: 'short', timeStyle: 'short' })}</span>
                {' · '}<strong>{row.admin_name}</strong>
                {' · '}{ACTIONS[row.action] || row.action}
                {row.detail && <> — {row.detail}</>}
              </li>
            ))}
          </ul>
        ))}
    </div>
  )
}

function OrdersTab({ api, t, me }) {
  const [orders, setOrders] = useState(null)
  const [openId, setOpenId] = useState('')
  const [filter, setFilter] = useState('active')
  const [users, setUsers] = useState([])
  const load = () => api.orders().then((d) => setOrders(d.orders)).catch(() => setOrders([]))
  useEffect(() => {
    load()
    api.users().then((d) => setUsers(d.users || [])).catch(() => setUsers([]))
  }, []) // eslint-disable-line

  const STATUS = {
    awaiting_payment: { label: t.stAwaiting, tone: 'warn' },
    payment_review: { label: t.stReview, tone: 'info' },
    paid: { label: t.stPaid, tone: 'ok' },
    fulfilled: { label: t.stFulfilled, tone: 'ok' },
    cancelled: { label: t.stCancelled, tone: 'danger' },
  }
  const DELIVERY = { pickup: t.dvPickup, delivery: t.dvDelivery }

  const filtered = (orders || []).filter((o) =>
    filter === 'all' ? true :
    filter === 'mine' ? o.handled_by === me?.id :
    filter === 'active' ? ['awaiting_payment', 'payment_review', 'paid'].includes(o.status) :
    o.status === filter)

  async function setStatus(id, status) {
    await api.setOrderStatus(id, status)
    load()
  }

  return (
    <section className="admin-body">
      <div className="admin-toolbar">
        <h2>{t.tabOrders} {orders ? `(${orders.length})` : ''}</h2>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="active">{t.fltActive}</option>
          {me && <option value="mine">{t.fltMine}</option>}
          <option value="payment_review">{t.stReview}</option>
          <option value="awaiting_payment">{t.stAwaiting}</option>
          <option value="paid">{t.stPaid}</option>
          <option value="fulfilled">{t.stFulfilled}</option>
          <option value="cancelled">{t.stCancelled}</option>
          <option value="all">{t.fltAll}</option>
        </select>
      </div>
      {orders === null ? <p className="muted">{t.loading}</p> :
        filtered.length === 0 ? <p className="muted">{t.noOrders}</p> : (
          <div className="admin-list">
            {filtered.map((o) => {
              const st = STATUS[o.status] || {}
              const items = JSON.parse(o.items)
              const open = openId === o.id
              return (
                <article key={o.id} className="admin-card">
                  <button className="admin-card-head" onClick={() => setOpenId(open ? '' : o.id)}>
                    <div>
                      <strong>{o.payment_reference}</strong> · {o.name}
                      <small className="muted"> — {new Date(o.created).toLocaleString(t.dateLocale, { dateStyle: 'short', timeStyle: 'short' })}</small>
                    </div>
                    <div>
                      {o.handled_by_name && <span className="admin-handler-chip">👤 {o.handled_by_name}</span>}
                      {o.payment_method === 'cash' && <span className="status-chip status-info">{t.pmCash}</span>}
                      {o.pickup_overdue && <span className="status-chip status-danger">{t.pickupLate}</span>}
                      <span className={`status-chip status-${st.tone}`}>{st.label}</span>
                      <strong className="admin-total">{aud(o.total)}</strong>
                    </div>
                  </button>
                  {open && (
                    <div className="admin-card-body">
                      <p>
                        📞 <a href={`tel:${o.phone}`}>{o.phone}</a>
                        {o.email && <> · ✉️ <a href={`mailto:${o.email}`}>{o.email}</a></>}
                      </p>
                      <p>🚚 {DELIVERY[o.delivery] || o.delivery}
                        {o.delivery !== 'pickup' && ` — ${o.address}, ${o.suburb} ${o.state} ${o.postcode}`}
                        {' · '}{o.payment_method === 'card' ? t.pmCard : o.payment_method === 'cash' ? t.pmCash : t.pmBank}</p>
                      {o.payment_method === 'cash' && o.pickup_by && (
                        <p className={o.pickup_overdue ? 'admin-pickup-late' : 'admin-pickup-due'}>
                          {o.pickup_overdue ? '⚠️ ' : '🕒 '}{t.pickupBy(o.pickup_by.label)}
                        </p>
                      )}
                      {o.notes && <p>📝 {o.notes}</p>}
                      <AssignRow api={api} order={o} users={users} me={me} t={t} onDone={load} />
                      <ul className="admin-items">
                        {items.map((it, i) => (
                          <li key={i}>
                            {it.qty} × {it.name}
                            {Object.keys(it.opts || {}).length > 0 && <small className="muted"> ({Object.values(it.opts).join(', ')})</small>}
                            <span>{aud(it.unitPrice * it.qty)}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="muted">
                        {t.lineSummary(aud(o.subtotal), aud(o.shipping), aud(o.gst))}<strong>{aud(o.total)}</strong>
                        {o.payment_reported_at && ` · ${t.reportedAt} ${new Date(o.payment_reported_at).toLocaleString(t.dateLocale)}`}
                      </p>
                      {o.receipt_type && <OrderReceipt api={api} id={o.id} type={o.receipt_type} t={t} />}
                      <div className="admin-actions">
                        {['awaiting_payment', 'payment_review'].includes(o.status) && (
                          <button className="btn btn-primary btn-sm" onClick={() => setStatus(o.id, 'paid')}>{t.btnConfirmPaid}</button>
                        )}
                        {['paid', 'fulfilled'].includes(o.status) && o.email && (
                          <ResendInvoice api={api} id={o.id} to={o.email} sent={Boolean(o.invoice_sent_at)} t={t} />
                        )}
                        {o.status === 'paid' && (
                          <button className="btn btn-primary btn-sm" onClick={() => setStatus(o.id, 'fulfilled')}>{t.btnFulfilled}</button>
                        )}
                        {!['cancelled', 'fulfilled'].includes(o.status) && (
                          <button className="btn btn-outline btn-sm" onClick={() => { if (confirm(t.confirmCancel)) setStatus(o.id, 'cancelled') }}>{t.btnCancel}</button>
                        )}
                        {o.status === 'cancelled' && (
                          <button className="btn btn-outline btn-sm" onClick={() => setStatus(o.id, 'awaiting_payment')}>{t.btnRestore}</button>
                        )}
                      </div>
                      <OrderHistory api={api} id={o.id} t={t} />
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        )}
    </section>
  )
}

/* ---------------- Products ---------------- */

function ProductsTab({ api, t }) {
  const [products, setProducts] = useState(null)
  const [editing, setEditing] = useState(null) // product object or 'new'
  const [reordering, setReordering] = useState(false)
  const [q, setQ] = useState('')
  const load = () => api.products().then((d) => setProducts(d.products)).catch(() => setProducts([]))
  useEffect(() => { load() }, []) // eslint-disable-line

  const filtered = (products || []).filter((p) =>
    !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.sku.toLowerCase().includes(q.toLowerCase()))

  if (editing) {
    return (
      <ProductEditor
        api={api}
        t={t}
        allProducts={products || []}
        product={editing === 'new' ? null : editing}
        onClose={(changed) => { setEditing(null); if (changed) load() }}
      />
    )
  }

  if (reordering) {
    return (
      <ReorderProducts
        api={api}
        t={t}
        products={products || []}
        onClose={(changed) => { setReordering(false); if (changed) load() }}
      />
    )
  }

  return (
    <section className="admin-body">
      <div className="admin-toolbar">
        <h2>{t.tabProducts} {products ? `(${products.length})` : ''}</h2>
        <input placeholder={t.searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-outline" onClick={() => setReordering(true)}>{t.btnReorder}</button>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>{t.btnAddProduct}</button>
      </div>
      {products === null ? <p className="muted">{t.loading}</p> : (
        <div className="admin-product-grid">
          {filtered.map((p) => {
            const imgs = JSON.parse(p.images || '[]')
            const prices = p.variations.length
              ? p.variations.map((v) => v.sale_price ?? v.price)
              : [p.sale_price ?? p.price].filter((x) => x != null)
            return (
              <button key={p.sku} className="admin-product-card" onClick={() => setEditing(p)}>
                <div className="admin-product-thumb">
                  <AdminImg src={imgs[0]} t={t} />
                </div>
                <div>
                  <strong>{p.name}</strong>
                  <small className="muted">{p.sku} · {catLabel(p)}{p.published ? '' : ` · ${t.hidden}`}</small>
                  <small>
                    {p.price_on_request
                      ? t.priceOnRequestBadge
                      : prices.length ? `${aud(Math.min(...prices))}${prices.length > 1 ? ` – ${aud(Math.max(...prices))}` : ''}` : t.noPrice}
                    {p.variations.length > 0 && ` · ${t.nVariants(p.variations.length)}`}
                  </small>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}

// Pick a category, move its products up and down, save. `sort` is one global number per
// product, so the list is renumbered in tens — that leaves room to slot something in by
// hand later and keeps the gaps meaningful.
function ReorderProducts({ api, t, products, onClose }) {
  const [category, setCategory] = useState(CATEGORIES[0])
  const [order, setOrder] = useState([])
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setSaved(false)
    setOrder(products
      .filter((p) => inCategory(p, category))
      .slice()
      .sort((a, b) => (a.sort ?? 999) - (b.sort ?? 999) || a.name.localeCompare(b.name)))
  }, [products, category])

  const move = (index, delta) => {
    const next = order.slice()
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    setOrder(next)
    setSaved(false)
  }

  async function save() {
    setSaving(true)
    try {
      await api.reorderProducts(order.map((p, i) => ({ sku: p.sku, sort: (i + 1) * 10 })))
      setSaved(true)
    } catch (e) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="admin-body">
      <div className="admin-toolbar">
        <h2>{t.reorderTitle}</h2>
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t.reorderPick}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button className="btn btn-outline" onClick={() => onClose(saved)}>{t.btnBackToList}</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !order.length}>
          {saving ? t.saving : saved ? t.reorderSaved : t.reorderSave}
        </button>
      </div>
      <p className="muted admin-reorder-hint">{t.reorderHint}</p>
      {order.length === 0 ? <p className="muted">{t.reorderEmpty}</p> : (
        <ol className="admin-reorder-list">
          {order.map((p, i) => {
            const imgs = (() => { try { return JSON.parse(p.images || '[]') } catch { return [] } })()
            return (
              <li key={p.sku}>
                <span className="admin-reorder-pos">{i + 1}</span>
                <div className="admin-reorder-thumb"><AdminImg src={imgs[0]} t={t} /></div>
                <div className="admin-reorder-name">
                  <strong>{p.name}</strong>
                  <small className="muted">{p.sku}{p.subcategory ? ` · ${p.subcategory}` : ''}{p.published ? '' : ` · ${t.hidden}`}</small>
                </div>
                <div className="admin-reorder-moves">
                  <button className="btn btn-outline btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label={t.reorderUp}>↑</button>
                  <button className="btn btn-outline btn-sm" onClick={() => move(i, 1)} disabled={i === order.length - 1} aria-label={t.reorderDown}>↓</button>
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}

// a product belongs to its main category plus anything in the `categories` JSON
const inCategory = (p, name) => {
  let extra = []
  try { extra = JSON.parse(p.categories || '[]') || [] } catch { /* ignore malformed */ }
  return p.category === name || extra.includes(name)
}

// "Hardware" or "Hardware + Tools & Accessories" when the product sits in several categories
const catLabel = (p) => {
  let extra = []
  try { extra = JSON.parse(p.categories || '[]') || [] } catch { /* ignore malformed */ }
  return [p.category, ...extra].filter(Boolean).join(' + ')
}

// the values box accepts "value=price" in add-up mode — the value is the part before "="
const splitValues = (s) => parseValueSpec(s).values
// short code used when auto-naming a generated variation, e.g. "Matte Black" → MATTEB
const abbreviate = (v) => String(v).replace(/[^A-Za-z0-9]+/g, '').toUpperCase().slice(0, 6) || 'X'

function ProductEditor({ api, t, product, allProducts = [], onClose }) {
  const isNew = !product
  const [form, setForm] = useState(() => product ? {
    sku: product.sku,
    name: product.name,
    category: product.category,
    subcategory: product.subcategory || '',
    short_desc: product.short_desc || '',
    description: product.description || '',
    price: product.price ?? '',
    sale_price: product.sale_price ?? '',
    stock: product.stock ?? '',
    price_on_request: Boolean(product.price_on_request),
    featured: Boolean(product.featured),
    published: Boolean(product.published),
    sort: product.sort ?? 999,
  } : {
    sku: '', name: '', category: 'Shower Screens', subcategory: '',
    short_desc: '', description: '', price: '', sale_price: '', stock: '',
    price_on_request: false, featured: false, published: true, sort: 999,
  })
  // หมวดเพิ่มเติม — สินค้าจะโชว์ในหมวดหลัก + หมวดพวกนี้
  const [extraCats, setExtraCats] = useState(() => {
    if (!product) return []
    try { return JSON.parse(product.categories || '[]') || [] } catch { return [] }
  })
  const [images, setImages] = useState(() => product ? JSON.parse(product.images || '[]') : [])
  // "goes well with" picks — empty means the storefront suggests matching parts itself
  const [related, setRelated] = useState(() => {
    if (!product) return []
    try { return JSON.parse(product.related || '[]') || [] } catch { return [] }
  })
  const [relatedQuery, setRelatedQuery] = useState('')
  const [variations, setVariations] = useState(() => product
    ? product.variations.map((v) => ({ ...v, opts: JSON.parse(v.opts), in_stock: Boolean(v.in_stock) }))
    : [])
  // Add-up pricing: the product is priced as base + a price per part chosen, instead of
  // one variation row per combination (five parts = ~10^6 rows, which nobody can fill in).
  const [addUpMode, setAddUpMode] = useState(() => {
    if (!product) return false
    try { return isAddUp(JSON.parse(product.options || '[]') || []) } catch { return false }
  })
  // Option list (name + allowed values) — drives the storefront steps and the variation columns.
  // Values are kept as free text while editing and split on commas when saved.
  const [options, setOptions] = useState(() => {
    if (!product) return []
    let saved = []
    try { saved = JSON.parse(product.options || '[]') || [] } catch { /* ignore malformed */ }
    const varOpts = product.variations.map((v) => JSON.parse(v.opts))
    const names = [...new Set([...saved.map((o) => o.name), ...varOpts.flatMap((o) => Object.keys(o))])]
    return names.map((name) => ({
      name,
      stockSku: saved.find((o) => o.name === name)?.stockSku || '',
      values: formatValueSpec(
        [...new Set([
          ...(saved.find((o) => o.name === name)?.values || []),
          ...varOpts.map((o) => o[name]).filter(Boolean),
        ])],
        saved.find((o) => o.name === name)?.prices,
        isAddUp(saved),
      ),
    }))
  })
  const [urlInput, setUrlInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))
  const namedOptions = options.filter((o) => o.name.trim())
  const optionNames = namedOptions.map((o) => o.name.trim())
  // colour/style option values on this product — used to tag a photo with the value it
  // represents. Pure sizes (e.g. "2100", "740-820") are skipped: photos track finishes, not mm.
  const isMeasure = (v) => /\d/.test(v) && /^[\d\s.,\-x×/]+(mm|cm|m)?$/i.test(v)
  const optionValueList = (() => {
    const set = new Set()
    namedOptions.forEach((o) => splitValues(o.values).forEach((v) => set.add(v)))
    variations.forEach((v) => Object.values(v.opts).forEach((val) => {
      const s = String(val ?? '').trim(); if (s) set.add(s)
    }))
    return [...set].filter((v) => !isMeasure(v))
  })()

  async function uploadFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setMsg(t.uploading)
    try {
      const { url } = await api.upload(file)
      setImages((imgs) => [...imgs, url])
      setMsg('')
    } catch (err) {
      setMsg(`${t.uploadFailed}: ${err.message}`)
    }
    e.target.value = ''
  }

  async function save() {
    if (!form.sku.trim() || !form.name.trim()) { setMsg(t.needSkuName); return }
    if (optionNames.some((n, i) => optionNames.indexOf(n) !== i)) { setMsg(t.dupOptionName); return }
    if (options.some((o) => !o.name.trim() && splitValues(o.values).length)) { setMsg(t.needOptionName); return }
    // every variation must answer every option, otherwise the storefront can never match it
    const blank = variations.findIndex((v) => optionNames.some((n) => !String(v.opts[n] ?? '').trim()))
    if (blank !== -1) { setMsg(t.blankOptionCell(blank + 1)); return }
    // a blank price would be stored as $0 and sold at $0 — catch it before saving
    const noPrice = variations.findIndex((v) => v.price === '' || v.price == null || !Number.isFinite(Number(v.price)))
    if (noPrice !== -1) { setMsg(t.blankPriceRow(noPrice + 1)); return }
    const emptyId = variations.findIndex((v) => !String(v.id ?? '').trim())
    if (emptyId !== -1) { setMsg(t.blankIdRow(emptyId + 1)); return }
    const ids = variations.map((v) => String(v.id).trim())
    const dupId = ids.find((id, i) => ids.indexOf(id) !== i)
    if (dupId) { setMsg(t.dupVariationId(dupId)); return }
    if (optionNames.length) {
      const combos = variations.map((v) => JSON.stringify(optionNames.map((n) => String(v.opts[n] ?? '').trim())))
      const dupCombo = combos.findIndex((c, i) => combos.indexOf(c) !== i)
      if (dupCombo !== -1) { setMsg(t.dupCombination(dupCombo + 1)); return }
    }
    setSaving(true)
    setMsg('')
    try {
      // option values = what the owner typed, plus anything actually used by a variation,
      // so the storefront steps always cover every variation
      const payloadOptions = optionNames.map((n) => {
        const raw = options.find((o) => o.name.trim() === n).values
        if (addUpMode) {
          // every value carries an explicit price (0 = free, e.g. a "None" part) — the
          // presence of this map is what puts the product in add-up mode
          const { values, prices } = parseValueSpec(raw)
          const stockSku = (options.find((o) => o.name.trim() === n).stockSku || '').trim()
          return {
            name: n,
            values,
            prices: Object.fromEntries(values.map((v) => [v, Number(prices[v]) || 0])),
            ...(stockSku ? { stockSku } : {}),
          }
        }
        return {
          name: n,
          values: [...new Set([
            ...splitValues(raw),
            ...variations.map((v) => String(v.opts[n] ?? '').trim()).filter(Boolean),
          ])],
        }
      }).filter((o) => o.values.length)
      // keep variation opts in option order and drop keys for deleted options
      const payloadVariations = variations.map((v) => ({
        ...v,
        opts: Object.fromEntries(optionNames.map((n) => [n, String(v.opts[n] ?? '').trim()])),
      }))
      await api.saveProduct({
        ...form,
        images,
        related,
        options: payloadOptions,
        categories: extraCats.filter((c) => c !== form.category),
      })
      if (addUpMode) await api.saveVariations(form.sku.trim(), [])
      else if (variations.length || !isNew) await api.saveVariations(form.sku.trim(), payloadVariations)
      onClose(true)
    } catch (err) {
      setMsg(`${t.saveFailed}: ${err.message}`)
      setSaving(false)
    }
  }

  async function remove() {
    if (!confirm(t.confirmDelete(form.name))) return
    await api.deleteProduct(form.sku)
    onClose(true)
  }

  const setVar = (i, k, value) =>
    setVariations((vs) => vs.map((v, idx) => (idx === i ? { ...v, [k]: value } : v)))

  function addVariation() {
    const optNames = optionNames.length ? optionNames : [t.optionDefault]
    if (!optionNames.length) setOptions([{ name: t.optionDefault, values: '' }])
    setVariations((vs) => {
      // count up until the id is free — rows may have been deleted, so length+1 can collide
      const ids = new Set(vs.map((v) => v.id))
      let n = vs.length + 1
      let id = `${form.sku}-${n}`
      while (ids.has(id)) id = `${form.sku}-${++n}`
      return [...vs, {
        id,
        opts: Object.fromEntries(optNames.map((nm) => [nm, ''])),
        price: '', sale_price: null, in_stock: true, stock: null,
      }]
    })
  }

  /* ----- options (e.g. Hardware Colour, Height (mm), Width (mm)) ----- */

  const setOption = (i, k, value) => {
    if (k === 'name') {
      const from = options[i].name
      const to = value
      // rename the key inside every variation so the table keeps its data
      setVariations((vs) => vs.map((v) => ({
        ...v,
        opts: Object.fromEntries(Object.entries(v.opts).map(([n, val]) => [n === from ? to : n, val])),
      })))
    }
    setOptions((os) => os.map((o, idx) => (idx === i ? { ...o, [k]: value } : o)))
  }

  function addOption(name = '') {
    if (name && options.some((o) => o.name.trim() === name)) return
    setOptions((os) => [...os, { name, values: '' }])
    if (name) setVariations((vs) => vs.map((v) => ({ ...v, opts: { ...v.opts, [name]: '' } })))
  }

  function removeOption(i) {
    const name = options[i].name
    if (variations.length && !confirm(t.confirmRemoveOption(name || t.optionDefault))) return
    setOptions((os) => os.filter((_, idx) => idx !== i))
    setVariations((vs) => vs.map((v) => {
      const opts = { ...v.opts }
      delete opts[name]
      return { ...v, opts }
    }))
  }

  function moveOption(i, dir) {
    const j = i + dir
    if (j < 0 || j >= options.length) return
    setOptions((os) => { const c = [...os]; [c[i], c[j]] = [c[j], c[i]]; return c })
  }

  // Build every combination of the option values, keeping prices/stock of rows that already exist.
  function generateVariations() {
    const grid = options
      .map((o) => ({ name: o.name.trim(), values: splitValues(o.values) }))
      .filter((o) => o.name && o.values.length)
    if (!grid.length) { setMsg(t.needOptionValues); return }
    const total = grid.reduce((n, o) => n * o.values.length, 1)
    if (total > 300) { setMsg(t.tooManyCombos(total)); return }

    let combos = [{}]
    for (const o of grid) combos = combos.flatMap((c) => o.values.map((val) => ({ ...c, [o.name]: val })))

    const key = (opts) => grid.map((o) => opts[o.name]).join('||')
    const existing = new Map(variations.map((v) => [key(v.opts), v]))
    // when a new option is added the old rows can't match exactly any more — inherit their
    // price from the row that agrees on every option it does answer (same colour, say)
    const closest = (opts) => variations.find((v) => Object.entries(v.opts).every(
      ([n, val]) => !val || opts[n] === undefined || opts[n] === val))
    const usedIds = new Set()
    const rows = combos.map((opts) => {
      const found = existing.get(key(opts))
      if (found) { usedIds.add(found.id); return { ...found, opts } }
      let id = [form.sku.trim() || 'SKU', ...grid.map((o) => abbreviate(opts[o.name]))].join('-')
      let n = 2
      while (usedIds.has(id)) id = `${id}-${n++}`
      usedIds.add(id)
      const base = closest(opts)
      return {
        id,
        opts,
        price: base?.price ?? '',
        sale_price: base?.sale_price ?? null,
        in_stock: base ? base.in_stock : true,
        stock: base?.stock ?? null,
      }
    })
    const kept = new Set(combos.map(key))
    const dropped = variations.filter((v) => !kept.has(key(v.opts))).length
    if (dropped > 0 && !confirm(t.confirmGenerate(rows.length, dropped))) return
    setVariations(rows)
    setMsg(t.generated(rows.length))
  }

  return (
    <section className="admin-body">
      <div className="admin-toolbar">
        <button className="btn btn-outline btn-sm" onClick={() => onClose(false)}>{t.back}</button>
        <h2>{isNew ? t.newProduct : `${t.editPrefix} ${product.name}`}</h2>
      </div>

      <div className="admin-editor">
        <div className="panel">
          <h3>{t.secInfo}</h3>
          <div className="field-row">
            <div className="field">
              <label>{t.fSku}</label>
              <input value={form.sku} onChange={set('sku')} disabled={!isNew} placeholder={t.fSkuPh} />
            </div>
            <div className="field">
              <label>{t.fCategory}</label>
              <select value={form.category} onChange={set('category')}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label>{t.fExtraCategories}</label>
            <div className="admin-cat-checks">
              {CATEGORIES.filter((c) => c !== form.category).map((c) => (
                <label key={c}>
                  <input
                    type="checkbox"
                    checked={extraCats.includes(c)}
                    onChange={(e) => setExtraCats((xs) => (e.target.checked ? [...xs, c] : xs.filter((x) => x !== c)))}
                  />
                  {c}
                </label>
              ))}
            </div>
            <p className="muted">{t.extraCategoriesHint}</p>
          </div>
          <div className="field">
            <label>{t.fName}</label>
            <input value={form.name} onChange={set('name')} />
          </div>
          <div className="field">
            <label>{t.fSubcategory}</label>
            <input value={form.subcategory} onChange={set('subcategory')} placeholder={t.fSubcategoryPh} />
          </div>
          <div className="field">
            <label>{t.fShortDesc}</label>
            <textarea rows={2} value={form.short_desc} onChange={set('short_desc')} />
          </div>
          <div className="field">
            <label>{t.fDescription}</label>
            <textarea rows={5} value={form.description} onChange={set('description')} />
          </div>
          {variations.length === 0 && (
            <>
              <div className="field-row">
                <div className="field">
                  <label>{t.fPrice}</label>
                  <input type="number" step="0.01" value={form.price} onChange={set('price')} />
                </div>
                <div className="field">
                  <label>{t.fSalePrice}</label>
                  <input type="number" step="0.01" value={form.sale_price ?? ''} onChange={set('sale_price')} />
                </div>
                <div className="field field-sm">
                  <label>{t.fStock}</label>
                  <input type="number" value={form.stock ?? ''} onChange={set('stock')} />
                </div>
              </div>
              <p className="muted">{t.stockHint}</p>
            </>
          )}
          <div className="field-row admin-checks">
            <label><input type="checkbox" checked={form.price_on_request} onChange={set('price_on_request')} /> {t.fPriceOnRequest}</label>
          </div>
          <p className="muted">{t.priceOnRequestHint}</p>
          <div className="field-row admin-checks">
            <label><input type="checkbox" checked={form.featured} onChange={set('featured')} /> {t.fFeatured}</label>
            <label><input type="checkbox" checked={form.published} onChange={set('published')} /> {t.fPublished}</label>
          </div>
        </div>

        <div className="panel">
          <h3>{t.secImages(images.length)}</h3>
          <div className="admin-images">
            {images.map((src, i) => (
              <div key={src + i} className="admin-image">
                <AdminImg src={src} t={t} />
                <FocusSelect src={src} t={t} onChange={(next) => setImages((im) => im.map((s, idx) => (idx === i ? next : s)))} />
                {optionNames.length > 0 && (
                  <StepTagSelect src={src} names={optionNames} t={t} onChange={(next) => setImages((im) => im.map((s, idx) => (idx === i ? next : s)))} />
                )}
                {optionValueList.length > 0 && (
                  <OptionTagSelect src={src} values={optionValueList} t={t} onChange={(next) => setImages((im) => im.map((s, idx) => (idx === i ? next : s)))} />
                )}
                <div className="admin-image-actions">
                  {i > 0 && <button onClick={() => setImages((im) => { const c = [...im]; [c[i - 1], c[i]] = [c[i], c[i - 1]]; return c })}>←</button>}
                  <button onClick={() => setImages((im) => im.filter((_, idx) => idx !== i))}>{t.del}</button>
                </div>
              </div>
            ))}
          </div>
          <div className="field">
            <label className="btn btn-outline btn-sm admin-upload">
              {t.btnUpload}
              <input type="file" accept="image/*" onChange={uploadFile} hidden />
            </label>
          </div>
          <div className="field-row">
            <input placeholder={t.urlPlaceholder} value={urlInput} onChange={(e) => setUrlInput(e.target.value)} />
            <button className="btn btn-outline btn-sm" onClick={() => { if (urlInput.trim()) { setImages((im) => [...im, urlInput.trim()]); setUrlInput('') } }}>{t.btnAddUrl}</button>
          </div>
        </div>

        <div className="panel">
          <h3>{t.secRelated(related.length)}</h3>
          <p className="muted">{t.relatedHint}</p>
          {related.length > 0 && (
            <ol className="admin-related-list">
              {related.map((sku, i) => {
                const p = allProducts.find((x) => x.sku === sku)
                return (
                  <li key={sku}>
                    <span className="admin-related-name">{p ? `${p.sku} — ${p.name}` : `${sku} ${t.relatedMissing}`}</span>
                    <span className="admin-image-actions">
                      {i > 0 && <button onClick={() => setRelated((r) => { const c = [...r]; [c[i - 1], c[i]] = [c[i], c[i - 1]]; return c })}>↑</button>}
                      <button className="link-danger" onClick={() => setRelated((r) => r.filter((x) => x !== sku))}>{t.del}</button>
                    </span>
                  </li>
                )
              })}
            </ol>
          )}
          <input
            placeholder={t.relatedSearchPh}
            value={relatedQuery}
            onChange={(e) => setRelatedQuery(e.target.value)}
          />
          {relatedQuery.trim() && (
            <div className="admin-related-results">
              {allProducts
                .filter((p) => p.sku !== form.sku && !related.includes(p.sku))
                .filter((p) => `${p.sku} ${p.name}`.toLowerCase().includes(relatedQuery.trim().toLowerCase()))
                .slice(0, 8)
                .map((p) => (
                  <button key={p.sku} className="btn btn-outline btn-sm" onClick={() => { setRelated((r) => [...r, p.sku]); setRelatedQuery('') }}>
                    + {p.sku} — {p.name}
                  </button>
                ))}
            </div>
          )}
        </div>

        <div className="panel admin-options">
          <h3>{t.secOptions(optionNames.length)}</h3>
          <p className="muted">{t.optionsHint}</p>
          {options.map((o, i) => (
            <div key={i} className="option-editor-row">
              <div className="field">
                <label>{t.fOptionName}</label>
                <input value={o.name} onChange={(e) => setOption(i, 'name', e.target.value)} placeholder={t.fOptionNamePh} />
              </div>
              <div className="field">
                <label>{t.fOptionValues}</label>
                <input value={o.values} onChange={(e) => setOption(i, 'values', e.target.value)} placeholder={t.fOptionValuesPh} />
              </div>
              {addUpMode && (
                <div className="field">
                  <label>{t.fStockSource}</label>
                  <select value={o.stockSku || ''} onChange={(e) => setOption(i, 'stockSku', e.target.value)}>
                    <option value="">{t.stockSourceNone}</option>
                    {allProducts
                      .filter((p) => p.sku !== form.sku)
                      .map((p) => <option key={p.sku} value={p.sku}>{p.sku} — {p.name}</option>)}
                  </select>
                </div>
              )}
              <div className="option-editor-actions">
                <button className="btn btn-outline btn-sm" onClick={() => moveOption(i, -1)} disabled={i === 0} title={t.moveUp}>↑</button>
                <button className="btn btn-outline btn-sm" onClick={() => moveOption(i, 1)} disabled={i === options.length - 1} title={t.moveDown}>↓</button>
                <button className="link-danger" onClick={() => removeOption(i)}>{t.del}</button>
              </div>
            </div>
          ))}
          {/* value suggestions for the variation table below */}
          {namedOptions.map((o, i) => (
            <datalist key={`dl-${i}`} id={`opt-values-${i}`}>
              {splitValues(o.values).map((v) => <option key={v} value={v} />)}
            </datalist>
          ))}
          <div className="option-presets">
            <span className="muted">{t.presetLabel}</span>
            {OPTION_PRESETS.filter((n) => !optionNames.includes(n)).map((n) => (
              <button key={n} className="btn btn-outline btn-sm" onClick={() => addOption(n)}>+ {n}</button>
            ))}
          </div>
          <div className="field-row">
            <button className="btn btn-outline btn-sm" onClick={() => addOption()}>{t.btnAddOption}</button>
            {!addUpMode && <button className="btn btn-primary btn-sm" onClick={generateVariations}>{t.btnGenerate}</button>}
          </div>
          <p className="muted">{addUpMode ? t.addUpHint : t.generateHint}</p>
          <label className="admin-addup-toggle">
            <input
              type="checkbox"
              checked={addUpMode}
              onChange={(e) => {
                const on = e.target.checked
                if (on && variations.length && !confirm(t.confirmAddUp(variations.length))) return
                if (on) setVariations([])
                setOptions((os) => os.map((o) => ({
                  ...o,
                  values: formatValueSpec(splitValues(o.values), parseValueSpec(o.values).prices, on),
                })))
                setAddUpMode(on)
              }}
            />
            <span>{t.addUpToggle}</span>
          </label>
        </div>

        <div className="panel admin-variations" hidden={addUpMode}>
          <h3>{t.secVariations(variations.length)}</h3>
          {variations.length === 0 ? (
            <p className="muted">{t.simplePriceHint}</p>
          ) : (
            <>
            <p className="muted">{t.stockHint}</p>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t.thCode}</th>
                  {optionNames.map((n) => <th key={n}>{n}</th>)}
                  <th>{t.thPrice}</th>
                  <th>{t.thSale}</th>
                  <th>{t.thStockQty}</th>
                  <th>{t.thStock}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {variations.map((v, i) => (
                  <tr key={i}>
                    <td><input value={v.id} onChange={(e) => setVar(i, 'id', e.target.value)} /></td>
                    {optionNames.map((n, oi) => (
                      <td key={n}>
                        <input
                          list={`opt-values-${oi}`}
                          value={v.opts[n] ?? ''}
                          onChange={(e) => setVar(i, 'opts', { ...v.opts, [n]: e.target.value })}
                        />
                      </td>
                    ))}
                    <td><input type="number" step="0.01" value={v.price} onChange={(e) => setVar(i, 'price', e.target.value)} /></td>
                    <td><input type="number" step="0.01" value={v.sale_price ?? ''} onChange={(e) => setVar(i, 'sale_price', e.target.value === '' ? null : e.target.value)} /></td>
                    <td><input type="number" value={v.stock ?? ''} onChange={(e) => setVar(i, 'stock', e.target.value === '' ? null : e.target.value)} /></td>
                    <td><input type="checkbox" checked={v.in_stock} onChange={(e) => setVar(i, 'in_stock', e.target.checked)} /></td>
                    <td><button className="link-danger" onClick={() => setVariations((vs) => vs.filter((_, idx) => idx !== i))}>{t.del}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
          <button className="btn btn-outline btn-sm" onClick={addVariation}>{t.btnAddVariation}</button>
        </div>
      </div>

      {msg && <p className="field-error">{msg}</p>}
      <div className="admin-actions admin-actions-sticky">
        <button className="btn btn-primary btn-lg" onClick={save} disabled={saving}>{saving ? t.saving : t.btnSaveProduct}</button>
        <button className="btn btn-outline" onClick={() => onClose(false)}>{t.btnCancelEdit}</button>
        {!isNew && <button className="btn btn-danger" onClick={remove}>{t.btnDeleteProduct}</button>}
      </div>
    </section>
  )
}

/* ---------------- Admin users ---------------- */

// One staff account. Owners can rename, change the role, switch it off, reset the
// password or delete it; everyone else sees the row read-only and can only change
// their own password.
function UserRow({ api, user, me, t, onChanged, setMsg }) {
  const [draft, setDraft] = useState(user)
  const [pwd, setPwd] = useState('')
  const [current, setCurrent] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => { setDraft(user) }, [user])

  const self = Boolean(me && !me.master && me.id === user.id)
  const canEdit = me?.role === 'owner'
  const dirty = draft.name !== user.name || draft.email !== user.email
    || draft.role !== user.role || Boolean(draft.active) !== Boolean(user.active)

  async function save() {
    setBusy(true)
    setMsg('')
    try {
      await api.saveUser({ id: user.id, name: draft.name, email: draft.email, role: draft.role, active: Boolean(draft.active) })
      await onChanged()
      setMsg(t.saved)
    } catch (err) {
      setMsg(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function savePassword() {
    setBusy(true)
    setMsg('')
    try {
      const res = await api.setUserPassword(user.id, pwd, current)
      setPwd('')
      setCurrent('')
      setShowPwd(false)
      // Changing your own password kills your session on purpose — log back in.
      setMsg(res.reauth ? t.passwordChangedSelf : t.passwordChanged)
    } catch (err) {
      setMsg(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!confirm(t.confirmDeleteUser(user.name || user.email))) return
    setBusy(true)
    setMsg('')
    try {
      await api.deleteUser(user.id)
      await onChanged()
    } catch (err) {
      setMsg(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <article className={`admin-card admin-user${user.active ? '' : ' admin-user-off'}`}>
      <div className="admin-card-body">
        <div className="field-row">
          <div className="field">
            <label>{t.fUserName}</label>
            <input value={draft.name || ''} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div className="field">
            <label>{t.fUserEmail}</label>
            <input type="email" value={draft.email} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </div>
          <div className="field">
            <label>{t.fUserRole}</label>
            <select value={draft.role} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
              <option value="staff">{t.roleStaff}</option>
              <option value="owner">{t.roleOwner}</option>
            </select>
          </div>
        </div>
        <p className="muted">
          {user.last_login ? t.userLastLogin(new Date(user.last_login).toLocaleString(t.dateLocale, { dateStyle: 'short', timeStyle: 'short' })) : t.userNever}
          {!user.active && <> · <strong>{t.userDisabled}</strong></>}
        </p>
        {canEdit && (
          <label className="admin-addup-toggle">
            <input type="checkbox" checked={Boolean(draft.active)} onChange={(e) => setDraft({ ...draft, active: e.target.checked ? 1 : 0 })} />
            {t.userActive}
          </label>
        )}
        {showPwd && (
          <div className="field-row">
            {self && (
              <div className="field">
                <label>{t.fCurrentPassword}</label>
                <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
              </div>
            )}
            <div className="field">
              <label>{t.fNewPassword}</label>
              <input type="password" autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
            </div>
            <div className="field">
              <label>&nbsp;</label>
              <button className="btn btn-primary btn-sm" disabled={busy || pwd.length < 8} onClick={savePassword}>{t.btnSaveUser}</button>
            </div>
          </div>
        )}
        <div className="admin-actions">
          {canEdit && dirty && <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{t.btnSaveUser}</button>}
          {(canEdit || self) && !showPwd && (
            <button className="btn btn-outline btn-sm" onClick={() => setShowPwd(true)}>
              {self ? t.btnChangeMyPassword : t.btnResetPassword}
            </button>
          )}
          {canEdit && !self && <button className="btn btn-outline btn-sm" disabled={busy} onClick={remove}>{t.btnDeleteUser}</button>}
        </div>
      </div>
    </article>
  )
}

function UsersTab({ api, t, me }) {
  const [users, setUsers] = useState(null)
  const [msg, setMsg] = useState('')
  const [adding, setAdding] = useState(false)
  const blank = { name: '', email: '', password: '', role: 'staff' }
  const [draft, setDraft] = useState(blank)
  const load = () => api.users().then((d) => setUsers(d.users)).catch(() => setUsers([]))
  useEffect(() => { load() }, []) // eslint-disable-line

  const canEdit = me?.role === 'owner'

  async function create() {
    setMsg('')
    try {
      await api.saveUser(draft)
      setDraft(blank)
      setAdding(false)
      await load()
      setMsg(t.saved)
    } catch (err) {
      setMsg(err.message)
    }
  }

  if (users === null) return <section className="admin-body"><p className="muted">{t.loading}</p></section>

  return (
    <section className="admin-body">
      <div className="admin-toolbar">
        <h2>{t.usersTitle} ({users.length})</h2>
        {canEdit && !adding && <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>{t.btnAddUser}</button>}
      </div>
      <p className="muted">{t.usersHint}</p>
      {me?.master && <p className="admin-master-hint">⚠️ {t.usersMasterHint}</p>}
      {adding && (
        <div className="panel">
          <div className="field-row">
            <div className="field">
              <label>{t.fUserName}</label>
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
            </div>
            <div className="field">
              <label>{t.fUserEmail}</label>
              <input type="email" autoComplete="off" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>{t.fUserPassword}</label>
              <input type="password" autoComplete="new-password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
            </div>
            <div className="field">
              <label>{t.fUserRole}</label>
              <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
                <option value="staff">{t.roleStaff}</option>
                <option value="owner">{t.roleOwner}</option>
              </select>
            </div>
          </div>
          <div className="admin-actions">
            <button className="btn btn-primary btn-sm" disabled={draft.password.length < 8 || !draft.email} onClick={create}>{t.btnSaveUser}</button>
            <button className="btn btn-outline btn-sm" onClick={() => { setAdding(false); setDraft(blank) }}>{t.btnCancelEdit}</button>
          </div>
        </div>
      )}
      {msg && <p className={msg.startsWith('✓') ? 'admin-ok' : 'field-error'}>{msg}</p>}
      {users.length === 0 ? <p className="muted">{t.noUsers}</p> : (
        <div className="admin-list">
          {users.map((u) => (
            <UserRow key={u.id} api={api} user={u} me={me} t={t} onChanged={load} setMsg={setMsg} />
          ))}
        </div>
      )}
    </section>
  )
}

/* ---------------- Settings ---------------- */

// Sends a sample tax invoice so the shop can prove email delivery works
// (and see exactly what the customer receives) without placing a real order.
function TestEmail({ api, t, defaultTo }) {
  const [to, setTo] = useState(defaultTo)
  const [state, setState] = useState({ busy: false, msg: '', ok: false })

  async function send() {
    setState({ busy: true, msg: '', ok: false })
    try {
      await api.testEmail(to)
      setState({ busy: false, ok: true, msg: t.testEmailSent(to) })
    } catch (err) {
      setState({ busy: false, ok: false, msg: err.message })
    }
  }

  return (
    <div className="test-email">
      <div className="field-row">
        <div className="field"><label>{t.fTestEmail}</label><input value={to} onChange={(e) => setTo(e.target.value)} placeholder="test@example.com" /></div>
        <button className="btn btn-outline" onClick={send} disabled={state.busy || !to.includes('@')}>
          {state.busy ? t.testEmailSending : t.btnTestEmail}
        </button>
      </div>
      {state.msg && <p className={state.ok ? 'admin-ok' : 'field-error'}>{state.msg}</p>}
    </div>
  )
}

function SettingsTab({ api, t }) {
  const [values, setValues] = useState(null)
  const [msg, setMsg] = useState('')
  useEffect(() => { api.settings().then((d) => setValues(d.settings)).catch(() => setValues({})) }, []) // eslint-disable-line

  if (!values) return <section className="admin-body"><p className="muted">{t.loading}</p></section>
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }))

  async function save() {
    setMsg('')
    try {
      await api.saveSettings(values)
      setMsg(t.saved)
    } catch (err) {
      setMsg(`${t.saveFailed}: ${err.message}`)
    }
  }

  return (
    <section className="admin-body">
      <div className="admin-toolbar"><h2>{t.tabSettings}</h2></div>
      <div className="admin-editor">
        <div className="panel">
          <h3>{t.secBank}</h3>
          <div className="field"><label>{t.fAccountName}</label><input value={values.bank_account_name || ''} onChange={set('bank_account_name')} /></div>
          <div className="field"><label>{t.fBank}</label><input value={values.bank_name || ''} onChange={set('bank_name')} placeholder={t.fBankPh} /></div>
          <div className="field-row">
            <div className="field"><label>{t.fBsb}</label><input value={values.bank_bsb || ''} onChange={set('bank_bsb')} placeholder="xxx xxx" /></div>
            <div className="field"><label>{t.fAccountNumber}</label><input value={values.bank_account_number || ''} onChange={set('bank_account_number')} /></div>
          </div>
          <div className="field"><label>{t.fPayid}</label><input value={values.payid || ''} onChange={set('payid')} /></div>
          <p className="muted">{t.bankHint}</p>
        </div>
        <div className="panel">
          <h3>{t.secEmail}</h3>
          <div className="field">
            <label>{t.fNotifyEmails}</label>
            <input value={values.notify_emails || ''} onChange={set('notify_emails')} placeholder="you@example.com, office@example.com" />
          </div>
          <div className="field">
            <label>{t.fEmailFrom}</label>
            <input value={values.email_from || ''} onChange={set('email_from')} placeholder="Level Up Glass Supply <orders@levelupglasssupply.com.au>" />
            <p className="muted">{t.emailFromHint}</p>
          </div>
          <p className="muted">{t.emailHint}</p>
          <TestEmail api={api} t={t} defaultTo={(values.notify_emails || '').split(/[,;\n]/)[0]?.trim() || ''} />
        </div>
        <div className="panel">
          <h3>{t.secShipping}</h3>
          <div className="field"><label>{t.fDelivery}</label><input type="number" value={values.shipping_delivery || ''} onChange={set('shipping_delivery')} /></div>
          <p className="muted">{t.shippingHint}</p>
          <label className="admin-addup-toggle">
            <input
              type="checkbox"
              checked={String(values.cash_enabled ?? '1') !== '0'}
              onChange={(e) => setValues((v) => ({ ...v, cash_enabled: e.target.checked ? '1' : '0' }))}
            />
            {t.fCashEnabled}
          </label>
          <p className="muted">{t.fCashEnabledHint}</p>
          <div className="field">
            <label>{t.fCashDays}</label>
            <input type="number" min="1" max="30" value={values.cash_pickup_days ?? '2'} onChange={set('cash_pickup_days')} />
            <p className="muted">{t.fCashDaysHint}</p>
          </div>
        </div>
        <CategoryImages api={api} t={t} values={values} setValues={setValues} setMsg={setMsg} />
      </div>
      {msg && <p className={msg.startsWith('✓') ? 'admin-ok' : 'field-error'}>{msg}</p>}
      <div className="admin-actions">
        <button className="btn btn-primary btn-lg" onClick={save}>{t.btnSaveSettings}</button>
      </div>
    </section>
  )
}

function CategoryImages({ api, t, values, setValues, setMsg }) {
  let meta = {}
  try { meta = JSON.parse(values.category_meta || '{}') } catch { meta = {} }

  const setImage = (slug, url) => {
    const next = { ...meta }
    if (url) next[slug] = url
    else delete next[slug]
    setValues((v) => ({ ...v, category_meta: JSON.stringify(next) }))
  }

  const upload = (slug) => async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setMsg(t.uploading)
    try {
      const { url } = await api.upload(file)
      setImage(slug, url)
      setMsg('')
    } catch (err) {
      setMsg(`${t.uploadFailed}: ${err.message}`)
    }
    e.target.value = ''
  }

  return (
    <div className="panel admin-variations">
      <h3>{t.secCategories}</h3>
      <div className="admin-cat-grid">
        {SITE_CATEGORIES.map((c) => {
          const current = meta[c.slug] || c.image
          return (
            <div key={c.slug} className="admin-cat-card">
              <div className="admin-product-thumb admin-cat-thumb">
                <AdminImg src={current} t={t} />
              </div>
              <strong>{c.name}</strong>
              <FocusSelect src={current} t={t} onChange={(next) => setImage(c.slug, next)} />
              <div className="admin-actions" style={{ marginTop: 8 }}>
                <label className="btn btn-outline btn-sm admin-upload">
                  {t.catUpload}
                  <input type="file" accept="image/*" onChange={upload(c.slug)} hidden />
                </label>
                {meta[c.slug] && (
                  <button className="btn btn-outline btn-sm" onClick={() => setImage(c.slug, null)}>{t.catReset}</button>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <p className="muted">{t.catHint}</p>
    </div>
  )
}
